import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@helpers/api';
import {
    DEFAULT_ACTIONS_BY_PLATFORM,
    WEBHOOK_METHODS,
    getActionTypesByPlatform,
    getDefaultConfigForType,
    newActionKey,
} from '../enums/actionTypes';

const EMPTY_INTEGRATION = {
    id: null,
    plan_id: '',
    event: 'payments.confirmed',
    platform: '',
    name: '',
    active: false,
    apiKey: '',
    endpoint: '',
};

function buildActionsFromRaw(rawActions = []) {
    return rawActions.map(a => ({
        _key: newActionKey(),
        id: a.id,
        type: a.type || a.action_type,
        config: a.config || a.action_config || {},
    }));
}

// Mirrors the backend webhook_call rules (HasWebhookActionRules) so the user
// gets a friendly message before hitting a 422. Masked secrets ("***xxxx")
// count as filled — echoing them back tells the backend to keep the stored value.
function validateWebhookConfig(t, config, position) {
    const c = config ?? {};
    const filled = v => typeof v === 'string' && v.trim().length > 0;

    const url = (c.url ?? '').trim();
    if (!url) return t('webhookBuilder.validation.urlRequired', { position });
    if (!/^https:\/\//i.test(url)) return t('webhookBuilder.validation.urlHttps', { position });
    try { new URL(url); } catch { return t('webhookBuilder.validation.urlInvalid', { position }); }

    if (!WEBHOOK_METHODS.includes(c.method)) {
        return t('webhookBuilder.validation.method', { position });
    }

    const auth = c.auth ?? {};
    if (auth.type === 'bearer' && !filled(auth.token)) {
        return t('webhookBuilder.validation.bearerToken', { position });
    }
    if (auth.type === 'basic' && (!filled(auth.username) || !filled(auth.password))) {
        return t('webhookBuilder.validation.basicCredentials', { position });
    }
    if (auth.type === 'api_key' && (!filled(auth.header_name) || !filled(auth.value))) {
        return t('webhookBuilder.validation.apiKeyHeader', { position });
    }

    const headerKeyPattern = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
    for (const h of c.headers ?? []) {
        if (!filled(h?.key) || !filled(h?.value)) {
            return t('webhookBuilder.validation.headerFields', { position });
        }
        if (!headerKeyPattern.test(h.key.trim())) {
            return t('webhookBuilder.validation.headerInvalidChars', { header: h.key, position });
        }
        if (['host', 'content-length'].includes(h.key.trim().toLowerCase())) {
            return t('webhookBuilder.validation.headerReserved', { header: h.key });
        }
    }

    const targets = new Set();
    for (const m of c.field_mappings ?? []) {
        if (!filled(m?.source)) {
            return t('webhookBuilder.validation.mappingSource', { position });
        }
        if (!filled(m?.target)) {
            return t('webhookBuilder.validation.mappingTarget', { position });
        }
        const target = m.target.trim();
        if (targets.has(target)) {
            return t('webhookBuilder.validation.mappingDuplicate', { target, position });
        }
        targets.add(target);
    }

    if (typeof c.body_template === 'string' && c.body_template.trim()) {
        try {
            const parsed = JSON.parse(c.body_template);
            if (parsed === null || typeof parsed !== 'object') throw new Error('not an object');
        } catch {
            return t('webhookBuilder.validation.bodyTemplateJson', { position });
        }
    }

    return null;
}

function buildDefaultActions(platform) {
    return (DEFAULT_ACTIONS_BY_PLATFORM[platform] ?? []).map(a => ({
        _key: newActionKey(),
        id: null,
        type: a.type,
        config: { ...a.config },
    }));
}

export default function useManageIntegration({ communityRef }) {
    const { t } = useTranslation('integrations');
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create'); // 'create' | 'edit'
    const [step, setStep] = useState(0); // 0 = integration form, 1 = actions (create only)
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [integration, setIntegration] = useState({ ...EMPTY_INTEGRATION });
    const [actions, setActions] = useState([]);
    const [removedActionIds, setRemovedActionIds] = useState([]);
    // Per-action test-fire state, keyed by action._key: { loading?, result?, error? }
    const [testStates, setTestStates] = useState({});

    // Integration created in step 1 (create flow) – need to track for cancel/rollback
    const createdIntegrationId = useRef(null);
    const callbackRef = useRef(() => { });

    // ─── Expose imperative API ───────────────────────────────────────────────

    // `platform` preselects the platform (the available-platform cards open
    // the modal already pointed at the one that was picked).
    const openCreate = useCallback((callback = () => { }, platform = '') => {
        callbackRef.current = callback;
        createdIntegrationId.current = null;
        setIntegration({ ...EMPTY_INTEGRATION, platform });
        setActions([]);
        setRemovedActionIds([]);
        setTestStates({});
        setStep(0);
        setError(null);
        setMode('create');
        setOpen(true);
    }, []);

    const openEdit = useCallback(async (integrationSummary, callback = () => { }) => {
        callbackRef.current = callback;
        createdIntegrationId.current = null;
        setError(null);
        setMode('edit');
        setStep(0);
        setLoading(true);
        setOpen(true);

        try {
            const res = await api.get(`integration/${integrationSummary.id}`);
            const data = res?.data ?? res;
            setIntegration({
                id: data.id,
                plan_id: data.plan ?? data.plan_id ?? '',
                event: data.event ?? '',
                platform: data.platform ?? '',
                name: data.name ?? '',
                active: data.active ?? true,
                apiKey: data.config?.apiKey ?? '',
                endpoint: data.config?.endpoint ?? '',
            });
            setActions(buildActionsFromRaw(data.actions ?? []));
            setRemovedActionIds([]);
            setTestStates({});
        } catch (e) {
            console.error(e);
            setError(t('manage.errors.loadIntegration'));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    // ─── Field helpers ───────────────────────────────────────────────────────

    const updateIntegration = useCallback((patch) => {
        setIntegration(prev => ({ ...prev, ...patch }));
    }, []);

    // ─── Validation ──────────────────────────────────────────────────────────

    function validateStep0() {
        if (!integration.platform) return t('manage.errors.platformRequired');
        if (!integration.plan_id) return t('manage.errors.planRequired');
        if (!integration.event) return t('manage.errors.eventRequired');
        if (!integration.name || integration.name.trim().length < 3) return t('manage.errors.nameMin');
        if (integration.name.trim().length > 50) return t('manage.errors.nameMax');
        if (integration.platform === 'activecampaign') {
            if (!integration.apiKey?.trim()) return t('manage.errors.apiKeyRequired');
            if (!integration.endpoint?.trim()) return t('manage.errors.endpointRequired');
        }
        return null;
    }

    function validateActions() {
        for (let i = 0; i < actions.length; i++) {
            const a = actions[i];
            if (a.type === 'webhook_call') {
                if (integration.platform === 'webhook') {
                    const err = validateWebhookConfig(t, a.config, i + 1);
                    if (err) return err;
                } else if (!a.config?.webhook_url?.trim()) {
                    return t('webhookBuilder.validation.urlRequired', { position: i + 1 });
                }
            }
            if (a.type === 'tag_add' && !a.config?.tag_id) {
                return t('manage.errors.tagRequired', { position: i + 1 });
            }
            if (a.type === 'list_add' && !a.config?.list_id) {
                return t('manage.errors.listRequired', { position: i + 1 });
            }
        }
        if (actions.length === 0) return t('manage.errors.actionsRequired');
        return null;
    }

    // ─── Create flow ─────────────────────────────────────────────────────────

    async function handleCreateStep0() {
        const err = validateStep0();
        if (err) { setError(err); return; }

        setError(null);
        setSubmitting(true);
        try {
            const community = communityRef.current;
            const payload = {
                community: community?.id,
                plan: integration.plan_id || null,
                event: integration.event,
                platform: integration.platform,
                name: integration.name.trim(),
                active: false, // will be activated after actions are saved
                ...(integration.platform === 'activecampaign' && {
                    config: {
                        apiKey: integration.apiKey.trim(),
                        endpoint: integration.endpoint.trim(),
                    },
                }),
            };
            const res = await api.post('integration', payload);
            const data = res?.data ?? res;
            if (res?.response?.status >= 400 || !data?.id) {
                setError(data?.message || t('manage.errors.create'));
                return;
            }
            createdIntegrationId.current = data.id;
            setIntegration(prev => ({ ...prev, id: data.id }));

            // Pre-populate default actions for the chosen platform
            setActions(buildDefaultActions(integration.platform));
            setStep(1);
        } catch (e) {
            console.error(e);
            setError(t('manage.errors.createRetry'));
        } finally {
            setSubmitting(false);
        }
    }

    async function handleCreateStep1() {
        const err = validateActions();
        if (err) { setError(err); return; }

        setError(null);
        setSubmitting(true);
        try {
            const integrationId = createdIntegrationId.current;
            for (let i = 0; i < actions.length; i++) {
                const a = actions[i];
                await api.post(`integration/${integrationId}/actions`, {
                    type: a.type,
                    config: a.config,
                    priority: i + 1,
                });
            }
            window.setSnackbar(t('feedback.created'));
            setOpen(false);
            callbackRef.current();
        } catch (e) {
            console.error(e);
            setError(t('manage.errors.saveActions'));
        } finally {
            setSubmitting(false);
        }
    }

    // ─── Edit flow ───────────────────────────────────────────────────────────

    async function handleEdit() {
        const err0 = validateStep0();
        if (err0) { setError(err0); return; }
        const errA = validateActions();
        if (errA) { setError(errA); return; }

        setError(null);
        setSubmitting(true);
        try {
            const integrationId = integration.id;

            // 1. Update integration name + active
            await api.patch(`integration/${integrationId}`, {
                name: integration.name.trim(),
                active: integration.active,
            });

            // 2. Delete removed actions
            for (const actionId of removedActionIds) {
                await api.delete(`integration/${integrationId}/actions/${actionId}`);
            }

            // 3. Update or create actions
            for (let i = 0; i < actions.length; i++) {
                const a = actions[i];
                if (a.id) {
                    await api.patch(`integration/${integrationId}/actions/${a.id}`, {
                        type: a.type,
                        config: a.config,
                        priority: i + 1,
                    });
                } else {
                    await api.post(`integration/${integrationId}/actions`, {
                        type: a.type,
                        config: a.config,
                        priority: i + 1,
                    });
                }
            }

            window.setSnackbar(t('feedback.updated'));
            setOpen(false);
            callbackRef.current();
        } catch (e) {
            console.error(e);
            setError(t('manage.errors.save'));
        } finally {
            setSubmitting(false);
        }
    }

    // ─── Actions management ──────────────────────────────────────────────────

    function handleAddAction() {
        const platform = integration.platform;
        const availableTypes = getActionTypesByPlatform(t)[platform] ?? [];
        const defaultType = availableTypes[0]?.value ?? 'webhook_call';
        setActions(prev => [
            ...prev,
            { _key: newActionKey(), id: null, type: defaultType, config: getDefaultConfigForType(defaultType, platform) },
        ]);
    }

    function handleUpdateAction(key, patch) {
        setActions(prev =>
            prev.map(a => a._key === key ? { ...a, ...patch } : a)
        );
        // A config change invalidates any previous test-fire result
        setTestStates(prev => (prev[key] ? { ...prev, [key]: null } : prev));
    }

    function handleRemoveAction(key) {
        setActions(prev => {
            const removed = prev.find(a => a._key === key);
            if (removed?.id) {
                setRemovedActionIds(ids => [...ids, removed.id]);
            }
            return prev.filter(a => a._key !== key);
        });
        setTestStates(prev => (prev[key] ? { ...prev, [key]: null } : prev));
    }

    // ─── Test fire (generic webhook) ─────────────────────────────────────────

    async function handleTestAction(key) {
        const index = actions.findIndex(a => a._key === key);
        if (index === -1) return;

        const action = actions[index];
        const integrationId = integration.id ?? createdIntegrationId.current;
        if (!integrationId) return;

        const validationError = validateWebhookConfig(t, action.config, index + 1);
        if (validationError) {
            setTestStates(prev => ({ ...prev, [key]: { error: validationError } }));
            return;
        }

        setTestStates(prev => ({ ...prev, [key]: { loading: true } }));
        try {
            // Config always goes inline so the test fires what is on screen,
            // saved or not; the backend swaps masked secrets for stored ones.
            const url = action.id
                ? `integration/${integrationId}/actions/${action.id}/test`
                : `integration/${integrationId}/actions/test`;
            const res = await api.post(url, { config: action.config });
            const data = res?.data ?? res;

            if (res?.response?.status >= 400 || typeof data?.response_code !== 'number') {
                const message = res?.response?.data?.message
                    || data?.message
                    || t('webhookBuilder.test.error');
                setTestStates(prev => ({ ...prev, [key]: { error: message } }));
                return;
            }

            setTestStates(prev => ({
                ...prev,
                [key]: {
                    result: {
                        response_code: data.response_code,
                        response_body: data.response_body ?? '',
                    },
                },
            }));
        } catch (e) {
            console.error(e);
            setTestStates(prev => ({ ...prev, [key]: { error: t('webhookBuilder.test.error') } }));
        }
    }

    async function handleToggle() {
        if (!integration.id) return;

        const previousActive = !!integration.active;

        // Optimistically update
        setIntegration(prev => ({ ...prev, active: !previousActive }));

        try {
            const res = await api.patch(`integration/${integration.id}/toggle`);
            if (res?.response?.status >= 400) {
                throw new Error(res?.response?.data?.message || t('manage.errors.toggleStatus'));
            }
            window.setSnackbar(!previousActive ? t('feedback.activated') : t('feedback.deactivated'));
        } catch (e) {
            console.error(e);
            window.setSnackbar(e.message || t('feedback.statusError'));
            // Revert
            setIntegration(prev => ({ ...prev, active: previousActive }));
        }
    }

    // ─── Modal control ───────────────────────────────────────────────────────

    async function handleClose() {
        // If we're on step 1 of creation (integration was already created), delete it
        if (mode === 'create' && step === 1 && createdIntegrationId.current) {
            try {
                await api.delete(`integration/${createdIntegrationId.current}`);
            } catch (e) {
                console.error('Failed to rollback integration on cancel', e);
            }
            createdIntegrationId.current = null;
        }
        setOpen(false);
        setError(null);
        callbackRef.current();
    }

    function handleBack() {
        if (mode === 'create' && step === 1) {
            setStep(0);
            setError(null);
        }
    }

    async function handleSubmit() {
        if (mode === 'create') {
            if (step === 0) await handleCreateStep0();
            else await handleCreateStep1();
        } else {
            await handleEdit();
        }
    }

    function handleSecondary() {
        if (mode === 'create' && step === 1) {
            handleBack();
        } else {
            handleClose();
        }
    }

    // ─── Derived ─────────────────────────────────────────────────────────────

    const isCreate = mode === 'create';
    const isStep0 = step === 0;
    const submitLabel = isCreate
        ? (isStep0 ? t('manage.submit.createIntegration') : t('manage.submit.createActions'))
        : t('manage.submit.edit');
    const cancelLabel = isCreate && step === 1 ? t('common:actions.back') : t('common:actions.cancel');
    const title = isCreate ? t('manage.title.create') : t('manage.title.edit');

    return {
        // modal state
        open,
        loading,
        submitting,
        error,
        // data
        mode,
        step,
        integration,
        actions,
        testStates,
        // handlers
        openCreate,
        openEdit,
        updateIntegration,
        handleAddAction,
        handleUpdateAction,
        handleRemoveAction,
        handleTestAction,
        handleToggle,
        handleSubmit,
        handleSecondary,
        handleClose,
        // ui
        submitLabel,
        cancelLabel,
        title,
        community: communityRef,
    };
}
